import {
  LibraWorflowStepComponentProps,
  StepValidationResult,
  UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import type { Dispatch, FC, SetStateAction } from 'react'
import { useEffect, useMemo } from 'react'
import styled from 'styled-components'

import { ConditionalFormConfig } from './conditionalForm/api/conditionalFormApi'
import { NiiAssessmentDetailsCard } from './niiReport/cards/NiiAssessmentDetailsCard'
import { NiiBasicInformationCard } from './niiReport/cards/NiiBasicInformationCard'
import { NiiConclusionCard } from './niiReport/cards/NiiConclusionCard'
import { NiiCoverageCard } from './niiReport/cards/NiiCoverageCard'
import { NiiInspectionsCard } from './niiReport/cards/NiiInspectionsCard'
import { niiReportConfigSchema } from './niiReport/niiReportApi'
import {
  NiiReportDataProvider,
  useNiiReportData,
} from './niiReport/NiiReportDataContext'
import { NiiSaveBanner } from './niiReport/NiiSaveBanner'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`

const StickySaveBannerContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  background-color: ${({ theme }) => theme.forecast.background1};
`

const NiiReportContent: FC<{
  setValidationResult: Dispatch<SetStateAction<StepValidationResult>>
}> = ({ setValidationResult }) => {
  const { hasChanges, resetChanges, doSave, isSaving, isLoading } =
    useNiiReportData()

  useEffect(() => {
    if (hasChanges) {
      setValidationResult({
        message: 'Changes have been made to the NII report',
        status: 'Warning',
      })
    } else {
      setValidationResult(undefined)
    }
  }, [hasChanges, setValidationResult])

  return (
    <Container>
      <StickySaveBannerContainer>
        <NiiSaveBanner
          hasChanges={hasChanges}
          isLoading={isLoading}
          isSaving={isSaving}
          onReset={resetChanges}
          onSave={doSave}
        />
      </StickySaveBannerContainer>
      <NiiBasicInformationCard />
      <NiiAssessmentDetailsCard />
      <NiiConclusionCard />
      <NiiCoverageCard />
      <NiiInspectionsCard />
    </Container>
  )
}

export const useValidateConfig: UseValidateConfigHook = ({ config }) => {
  return useMemo(
    () => niiReportConfigSchema.safeParse(config).success,
    [config]
  )
}

export const Component: FC<
  LibraWorflowStepComponentProps<ConditionalFormConfig>
> = ({ config, setValidationResult }) => {
  const niiConfig = niiReportConfigSchema.parse(config)
  return (
    <NiiReportDataProvider niiConfig={niiConfig}>
      <NiiReportContent setValidationResult={setValidationResult} />
    </NiiReportDataProvider>
  )
}
